import React, {useCallback} from 'react';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import I18n from 'app/i18n';
import {Link} from 'react-router-dom';
import {getLinkPath} from 'app/utils/link_helper';
import {getImageByBonusesType, getKeyByType} from 'app/components/my_profile/bonuses/bonuses_utils';
import truncateText from 'app/utils/truncateText';
import BonusesCardTime from 'app/components/my_profile/bonuses/BonusesCardTime';
import ArticleInterface from 'app/interfaces/articles/ArticleInterface';
import {configStore} from 'app/store/global';
import {scrollToTop} from 'app/utils';
import ParticipatePromoButton from 'app/components/reusable/ParticipatePromoButton';
import {REPEATABILITY_ONE_TIME} from 'app/utils/Consts';
import {pushAnalyticsEvent} from 'app/utils/analytics';

interface PromoBonusesCardProps extends ArticleInterface {
    hideExpiredPromoNewData: (id: number, type: string) => void
}

const MAX_TITLE_LENGTH = 30;

const getLabelByLocale = (data: ArticleInterface, column = 'title'): string => data[`promo_${column}_langs`] &&
    data[`promo_${column}_langs`][I18n.locale] ||
    data[`promo_${column}`] || data[column] || data[`promo_${column}_langs`][configStore.DEFAULT_LOCALE];

const PromoBonusesCard: React.FC<PromoBonusesCardProps> = props => {
    const {id, ended_at, bonus_sum, bonus_type, birthday, participation, slug, repeatability} = props;
    const bonusSum = Math.trunc(bonus_sum);
    const fraction = Math.trunc((bonus_sum - bonusSum) * 100);
    const title = truncateText(getLabelByLocale(props) ?? '', MAX_TITLE_LENGTH);

    const isParticipating = participation?.participating;
    const isCompleted = participation?.completed;
    const progress = participation?.progress;

    if (isCompleted && repeatability === REPEATABILITY_ONE_TIME) {
        return null;
    }

    const hideExpiredBonus = useCallback(() => {
        props.hideExpiredPromoNewData(id, `${props.promo_type}`);
    }, [id, props.promo_type]);

    const handlePromotionClick = useCallback(() => {
        pushAnalyticsEvent('promotion_click', {
            promotion_id: id,
            promotion_type: props.promo_type,
            source: 'promo_bonus_card'
        });
        scrollToTop();
    }, [id, props.promo_type]);

    const progressPercentage = progress?.percentage || 0;

    return <div className="bonuses-card bonuses-card--locked">
        <div className="bonuses-card-wrapper">
            <div className="bonuses-card__img">
                <img src={getImageByBonusesType(bonus_type, birthday)} alt={getKeyByType(bonus_type, birthday)}/>
            </div>
            <p className="bonuses-card__amount">
                    ${bonusSum || ''}
                {fraction > 0 && <span className="bonuses-card__amount-fraction">.{fraction}</span>}
            </p>
            <div className="bonuses-card-turnover">
                <p className="bonuses-card-turnover__title">
                    <span>{I18n.t('bonuses_page.progress')}</span>
                </p>
                <div className="bonuses-card__progress">
                    <div className="bonuses-card__progress-background">
                        <div
                            className="bonuses-card__progress-fill"
                            style={{width: `${Math.min(progressPercentage, 100)}%`}}
                        />
                    </div>
                </div>
            </div>
            <BonusesCardTime timeLeft={ended_at} hideExpiredBonus={hideExpiredBonus}/>
        </div>
        <div className="bonuses-card-wrapper-locked">
            <p className="bonuses-card-wrapper-locked__title">{I18n.t('bonuses_page.receiving')}</p>
            <p className="bonuses-card-wrapper-locked__text">
                {I18n.t('bonuses_page.participation_in_promo', {promoName: title})}
            </p>
            <ParticipatePromoButton
                isParticipating={isParticipating}
                promoId={id}
                title={title}
                authPlacement="PROMO_BONUSES_CARD"
                className="bonuses-card__btn"
            />
        </div>
        <Link
            to={getLinkPath(`/promotions/${slug}`)}
            className="bonuses-card__help"
            onClick={handlePromotionClick}
        >
            <SpriteIcon name="#svg-question" size={{height: 16, width: 16}}/>
        </Link>
        <div className="bonuses-card__lock">
            <SpriteIcon name="#svg-lock-transparent" size={{height: 16, width: 16}}/>
        </div>
    </div>;
};

export default PromoBonusesCard;
